import React from "react";
import { motion } from "framer-motion";
import SEO from "@/components/site/SEO";
import SectionHeading from "@/components/site/SectionHeading";
import FinalCTA from "@/components/site/FinalCTA";
import { CASE_STUDIES } from "@/data/content";

export default function WorkPage({ onStartProject }) {
    return (
        <div data-testid="page-work">
            <SEO
                title="Work — Featured Case Studies & Client Outcomes"
                description="Featured case studies from IdeaClap: AI-powered lead generation, brand launches, agentic operations, and compounding content engines. Real clients. Measured outcomes."
                path="/work"
                keywords="AI marketing case studies, brand launch case study, AI lead generation, content engine case study, agentic operations example"
            />
            <header className="relative pt-32 lg:pt-40 pb-16 overflow-hidden">
                <div className="absolute inset-0 hero-radial pointer-events-none" />
                <div className="relative mx-auto max-w-7xl container-px">
                    <SectionHeading
                        eyebrow="Featured Work"
                        title={
                            <>
                                Systems we’ve built.{" "}
                                <span className="italic text-[hsl(var(--text-muted))]">Outcomes that compound.</span>
                            </>
                        }
                        subtitle="Selected engagements from our partner network. Each engagement leaves an owned engine, agent stack, or growth system behind."
                    />
                </div>
            </header>

            <section className="mx-auto max-w-7xl container-px py-12 pb-24 lg:pb-28">
                <div className="flex flex-col gap-16 lg:gap-20">
                    {CASE_STUDIES.map((c, i) => {
                        const reverse = i % 2 === 1;
                        return (
                            <article
                                key={c.id}
                                id={c.id}
                                data-testid="work-detail-card"
                                className="grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-12"
                            >
                                <motion.div
                                    initial={{ opacity: 0, y: 16 }}
                                    whileInView={{ opacity: 1, y: 0 }}
                                    viewport={{ once: true, margin: "-10% 0px" }}
                                    transition={{ duration: 0.6, ease: [0.16, 1, 0.3, 1] }}
                                    className={`lg:col-span-7 ${reverse ? "lg:order-2" : ""}`}
                                >
                                    <div className="relative overflow-hidden rounded-3xl border border-black/[0.08] bg-white aspect-[16/10] shadow-soft">
                                        <img
                                            src={c.cover}
                                            alt={`${c.client} cover`}
                                            className="absolute inset-0 h-full w-full object-cover"
                                            loading="lazy"
                                        />
                                        <div
                                            className="absolute inset-0"
                                            style={{
                                                background:
                                                    "linear-gradient(180deg, rgba(255,255,255,0) 60%, rgba(255,255,255,0.4) 100%)",
                                            }}
                                        />
                                    </div>
                                </motion.div>
                                <motion.div
                                    initial={{ opacity: 0, y: 16 }}
                                    whileInView={{ opacity: 1, y: 0 }}
                                    viewport={{ once: true, margin: "-10% 0px" }}
                                    transition={{ duration: 0.6, delay: 0.05, ease: [0.16, 1, 0.3, 1] }}
                                    className={`lg:col-span-5 ${reverse ? "lg:order-1" : ""} flex flex-col gap-5 justify-center`}
                                >
                                    <div className="flex flex-wrap items-center gap-2 text-[11px] font-mono uppercase tracking-[0.22em] text-[hsl(var(--text-subtle))]">
                                        <span>{c.client}</span>
                                        <span className="text-[hsl(var(--text-subtle))]/50">/</span>
                                        <span>{c.category}</span>
                                    </div>
                                    <h2 className="font-display text-3xl sm:text-4xl lg:text-[44px] leading-[1.05] tracking-[-0.02em] text-[hsl(var(--text))]">
                                        {c.title}
                                    </h2>
                                    <p className="text-[15px] leading-relaxed text-[hsl(var(--text-muted))]">
                                        {c.summary}
                                    </p>
                                    <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
                                        {c.metrics.map((m) => (
                                            <div
                                                key={m.label}
                                                className="rounded-xl border border-black/[0.08] bg-white p-3"
                                            >
                                                <div className="font-display text-[22px] text-[hsl(var(--gold))] num-tabular">
                                                    {m.value}
                                                </div>
                                                <div className="text-[10.5px] font-mono uppercase tracking-[0.22em] text-[hsl(var(--text-subtle))] mt-0.5">
                                                    {m.label}
                                                </div>
                                            </div>
                                        ))}
                                    </div>
                                    <details className="mt-2 group">
                                        <summary className="cursor-pointer text-[13px] text-[hsl(var(--text-muted))] hover:text-[hsl(var(--text))] inline-flex items-center gap-2">
                                            <span className="h-1.5 w-1.5 rounded-full bg-[hsl(var(--blue))] group-open:bg-[hsl(var(--gold))] transition-colors" />
                                            Read approach
                                        </summary>
                                        <div className="mt-4 grid grid-cols-1 gap-4 text-[14px] text-[hsl(var(--text-muted))] leading-relaxed">
                                            <Block label="Challenge" body={c.challenge} />
                                            <Block label="Approach" body={c.approach} />
                                            <Block label="Outcome" body={c.outcome} />
                                        </div>
                                    </details>
                                </motion.div>
                            </article>
                        );
                    })}
                </div>
            </section>

            <FinalCTA onStartProject={onStartProject} />
        </div>
    );
}

const Block = ({ label, body }) => (
    <div className="rounded-xl border border-black/[0.08] bg-[hsl(var(--bg-2))] p-4">
        <div className="text-[10.5px] font-mono uppercase tracking-[0.22em] text-[hsl(var(--text-subtle))]">
            {label}
        </div>
        <p className="mt-1.5 text-[hsl(var(--text))]">{body}</p>
    </div>
);
